import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CustomInput from "../../../components/CustomInput";
import CustomDropdown from "../../../components/CustomDropdown";
import CustomButton from "../../../components/CustomButton";
import Sidebar from "../../../components/Sidebar";
import AppConstants from "../../../utils/appConstants";
import { useToast } from "../../../contexts/ToastContext";
import { openSupportChatOnCurrentPage } from "../../../utils/supportChatNavigation";
import { fetchAgentClients } from "../../../controller/ibDashboard/clientsController";
import WalletController from "../../../controller/walletController";
import {
  createTransferToClientOperation,
} from "../../../controller/ibDashboard/transferToClientController";
import TransactionPasswordDialog from "../../../components/TransactionPasswordDialog";
import "../../css/cachIn.css";

// Helper to safely extract a displayable string from potentially JSON-ish values
const extractValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (
      (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
      (trimmed.startsWith("[") && trimmed.endsWith("]"))
    ) {
      try {
        const parsed = JSON.parse(trimmed);
        if (
          typeof parsed === "object" &&
          parsed !== null &&
          !Array.isArray(parsed)
        ) {
          const values = Object.values(parsed);
          return values.length > 0 ? String(values[0]).trim() : "";
        }
        return trimmed;
      } catch {
        return value;
      }
    }
    return value;
  }
  if (typeof value === "object" && value !== null) {
    if (Array.isArray(value)) {
      return value.length > 0 ? extractValue(value[0]) : "";
    }
    const values = Object.values(value);
    const extracted = values.length > 0 ? String(values[0]).trim() : "";
    return extracted;
  }
  return String(value).trim();
};

// Helper: get client nickname (same strategy as IbClientsList)
const getClientNickName = (client) => {
  return (
    client?.NICK_NAME ||
    client?.NICK ||
    client?.nick ||
    client?.nick_name ||
    client?.NICKNAME ||
    "-"
  );
};

export default function TransferToClient() {
  const { t } = useTranslation("mobile");
  const { success: showSuccess, error: showError, warning: showWarning } =
    useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [errors, setErrors] = useState({});

  // Form fields
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedWallet, setSelectedWallet] = useState(null);

  // Data lists
  const [clients, setClients] = useState([]);
  const [wallets, setWallets] = useState([]);

  // Password dialog
  const [showPasswordPopup, setShowPasswordPopup] = useState(false);
  const [passwordDialogData, setPasswordDialogData] = useState(null);

  // Basic permission gate: IB transfer is allowed only for logged-in agents
  const canProceed = useMemo(() => {
    // For now just ensure there is a token – mirrors other money operations.
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");
    return !!token;
  }, []);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        setErrors({});

        // Load user from sessionStorage if available (same pattern as CashIn)
        let userData = null;
        try {
          const storedUserData = sessionStorage.getItem("userData");
          const storedTimestamp = sessionStorage.getItem("userDataTimestamp");
          if (storedUserData && storedTimestamp) {
            const age = Date.now() - parseInt(storedTimestamp, 10);
            if (age < 5 * 60 * 1000) {
              userData = JSON.parse(storedUserData);
              setUser(userData);
            }
          }
        } catch {
          // ignore cache issues
        }

        // Fetch IB clients (use same parsing strategy as IbClientsList)
        try {
          const response = await fetchAgentClients({
            page: 0,
            withDoc: 0,
            search: null,
          });

          let data = [];

          if (response) {
            if (Array.isArray(response.DATA)) {
              data = response.DATA;
            } else if (response.DATA && Array.isArray(response.DATA.clients)) {
              data = response.DATA.clients;
            } else if (response.DATA && Array.isArray(response.DATA.data)) {
              data = response.DATA.data;
            } else if (Array.isArray(response)) {
              data = response;
            } else if (Array.isArray(response.clients)) {
              data = response.clients;
            } else if (Array.isArray(response.data)) {
              data = response.data;
            } else if (response.DATA && !Array.isArray(response.DATA)) {
              data = [response.DATA];
            } else if (!Array.isArray(response)) {
              data = [response];
            }
          }

          const clientOptions = data
            .map((c) => ({
              value: c.ID ?? c.USER_ID ?? c.UserId ?? c.id,
              label: getClientNickName(c),
              data: c,
            }))
            .filter((c) => c.value != null && c.value !== "");

          setClients(clientOptions);
        } catch {
          setClients([]);
        }

        // Fetch IB wallets (reuse WalletController like CashIn, but for current user/IB)
        try {
          const walletData = await WalletController.getWallets();

          if (walletData && walletData.wallets && Array.isArray(walletData.wallets)) {
            const walletOptions = walletData.wallets
              .filter((wallet) => {
                // Mirror Flutter: exclude TRADING (3) and LOCAL (4) for IB transfer.
                const typeVal =
                  wallet.currencies?.TYPE ??
                  wallet.currency?.TYPE ??
                  wallet.currency?.type ??
                  wallet.CURRENCY_TYPE ??
                  wallet.currencyType ??
                  null;
                const typeNum =
                  typeof typeVal === "string" ? parseInt(typeVal, 10) : typeVal;
                return typeNum !== 3 && typeNum !== 4;
              })
              .map((wallet) => {
              const currencyCode =
                wallet.currencies?.CURRENCY_CODE || wallet.CURRENCY_CODE || "";
              const currencySymbol =
                wallet.currencies?.CURRENCY_SYMBOL ||
                wallet.CURRENCY_SYMBOL ||
                "";
              const balance =
                wallet.CURRENT_BALENCE ||
                wallet.CURRENT_BALANCE ||
                wallet.BALANCE ||
                wallet.AMOUNT ||
                0;

              const balanceNum =
                typeof balance === "string"
                  ? parseFloat(balance.replace(/,/g, ""))
                  : parseFloat(balance);
              const formattedBalance = isNaN(balanceNum) ? 0 : balanceNum;

              return {
                value: wallet.ID ?? wallet.id,
                label: `${formattedBalance.toFixed(2)} ${
                  currencyCode || currencySymbol
                }`,
                data: wallet,
              };
            })
              .filter((w) => w.value != null && w.value !== "");

            setWallets(walletOptions);
          } else {
            setWallets([]);
          }
        } catch {
          setWallets([]);
        }
      } catch (error) {
        showError(
          "Error Occurred",
          error?.message || "Failed to load transfer information"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [showError]);

  const handleAmountChange = (e) => {
    const value = e.target.value;
    setAmount(value);
    if (errors.amount) {
      setErrors((prev) => ({ ...prev, amount: null }));
    }
  };

  const handleNoteChange = (e) => {
    setNote(e.target.value);
    if (errors.note) {
      setErrors((prev) => ({ ...prev, note: null }));
    }
  };

  const handleClientChange = (value) => {
    setSelectedClient(value);
    if (errors.client) {
      setErrors((prev) => ({ ...prev, client: null }));
    }
  };

  const handleWalletChange = (value) => {
    setSelectedWallet(value);
    if (errors.wallet) {
      setErrors((prev) => ({ ...prev, wallet: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!amount || amount.trim() === "") {
      newErrors.amount = "Amount is required";
    } else {
      const amountValue = parseFloat(amount.trim().replace(/,/g, ""));
      if (isNaN(amountValue) || amountValue <= 0) {
        newErrors.amount = "Amount must be a valid positive number";
      }
    }

    if (!selectedClient) {
      newErrors.client = "Client is required";
    }

    if (!selectedWallet) {
      newErrors.wallet = "Wallet is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProceed = async () => {
    if (!validateForm()) {
      return;
    }

    const amountValue = parseFloat(amount.trim().replace(/,/g, ""));
    const clientId =
      typeof selectedClient === "object"
        ? selectedClient.value
        : selectedClient;

    const walletId =
      typeof selectedWallet === "object"
        ? selectedWallet.value
        : selectedWallet;

    if (clientId == null || clientId === "") {
      setErrors((prev) => ({ ...prev, client: "Invalid client selection" }));
      return;
    }
    if (walletId == null || walletId === "") {
      setErrors((prev) => ({ ...prev, wallet: "Invalid wallet selection" }));
      return;
    }

    // Transfer-to-client spends from the current user's selected wallet.
    // Mirror expected UX by validating local balance before calling backend.
    const selectedWalletData =
      typeof selectedWallet === "object" ? selectedWallet.data : null;
    const walletBalanceRaw =
      selectedWalletData?.CURRENT_BALENCE ??
      selectedWalletData?.CURRENT_BALANCE ??
      selectedWalletData?.BALANCE ??
      selectedWalletData?.AMOUNT ??
      0;
    const walletBalance =
      typeof walletBalanceRaw === "string"
        ? parseFloat(walletBalanceRaw.replace(/,/g, ""))
        : Number(walletBalanceRaw);
    if (!Number.isNaN(walletBalance) && amountValue > walletBalance) {
      showError(
        "Error Occurred",
        "You don't have enough amount in the selected wallet."
      );
      return;
    }

    try {
      setProcessing(true);
      setErrors({});

      const createResp = await createTransferToClientOperation({
        amount: amountValue,
        walletId,
        userId: clientId,
        note: note.trim(),
        type: 4,
      });

      const code = Number(
        createResp?.CODE ?? createResp?.code ?? createResp?.Code ?? 0
      );

      if (code !== AppConstants.responseOk && code !== 200) {
        const message =
          createResp?.MESSAGE ||
          createResp?.message ||
          createResp?.ERROR ||
          (code
            ? `Failed to create transfer operation (CODE: ${code}).`
            : "Failed to create transfer operation.");
        showError("Error Occurred", message);
        setProcessing(false);
        return;
      }

      // Backend should return TRANSACTION_ID for apply step
      const transactionId =
        createResp?.TRANSACTION_ID ||
        createResp?.REQUEST_TOKEN ||
        createResp?.transactionId;

      if (!transactionId) {
        showError(
          "Error Occurred",
          "Missing transaction identifier from server response."
        );
        setProcessing(false);
        return;
      }

      const firstMsg = `You will transfer ${amountValue} to the selected client. Confirm with your PIN / password to proceed.`;

      setPasswordDialogData({
        requestToken: transactionId,
        firstMessage: firstMsg,
        secondMessage: "",
      });
      setShowPasswordPopup(true);
    } catch (error) {
      showError(
        "Error Occurred",
        error?.message || "Failed to process transfer. Please try again."
      );
    } finally {
      setProcessing(false);
    }
  };

  const refreshLocalWallets = async () => {
    try {
      const walletData = await WalletController.getWallets(true);
      if (walletData && walletData.wallets && Array.isArray(walletData.wallets)) {
        const updatedResult = await WalletController.updateEWalletsAmount(walletData.wallets);
        
        if (updatedResult && updatedResult.wallets) {
          try {
            const storedUserData = sessionStorage.getItem('userData');
            if (storedUserData) {
              const ud = JSON.parse(storedUserData);
              if (ud.account && Array.isArray(ud.account) && ud.account.length > 0) {
                ud.account[0].wallet = updatedResult.wallets;
                sessionStorage.setItem('userData', JSON.stringify(ud));
                sessionStorage.setItem('userDataTimestamp', Date.now().toString());
              }
            }
          } catch (_) {}
          
          const walletOptions = updatedResult.wallets.map((w) => {
            const currencyCode = w.currencies?.CURRENCY_CODE || w.CURRENCY_CODE || "";
            const currencySymbol = w.currencies?.CURRENCY_SYMBOL || w.CURRENCY_SYMBOL || "";
            const balance = w.CURRENT_BALENCE || w.CURRENT_BALANCE || w.BALANCE || w.AMOUNT || 0;
            const balanceNum = typeof balance === "string"
              ? parseFloat(balance.replace(/,/g, ""))
              : parseFloat(balance);
            const formattedBalance = isNaN(balanceNum) ? 0 : balanceNum;
            return {
              value: w.ID ?? w.id,
              label: `${formattedBalance.toFixed(2)} ${currencyCode || currencySymbol}`,
              data: w,
            };
          });
          setWallets(walletOptions);
        }
      }
    } catch (_) {}
  };

  const handlePasswordDialogClose = async (result) => {
    setShowPasswordPopup(false);
    setPasswordDialogData(null);

    if (result === "ok" || result === "need_admin") {
      showWarning(
        "Request Submitted",
        result === "ok"
          ? "Transfer request submitted successfully."
          : "Transfer pending admin approval."
      );
      setErrors({});
      setAmount("");
      setNote("");
      setSelectedClient(null);
      setSelectedWallet(null);
    } else if (result === "canceled") {
      showWarning("Action Canceled", "Transaction canceled.");
      setErrors({});
      setAmount("");
      setNote("");
      setSelectedClient(null);
      setSelectedWallet(null);
    } else if (result === "failed") {
      showError(
        "Error Occurred",
        "Transaction failed. Please check your PIN / password or try again."
      );
    }

    await refreshLocalWallets();
  };

  return (
    <div className="cash-in-page cash-out-page">
      <Sidebar user={user} />
      <div className="cash-in-content">
        {loading && <div className="cash-in-loading-message">{t("loading")}...</div>}

        <div className="cash-in-container cash-in-container--styled">
          <div className="cash-in-tab-content cash-in-tab-content--styled">
            {!canProceed ? (
              <div className="cash-in-locked-message">
                <i className="fa-solid fa-lock"></i>
                <p>{t("you_dont_have_access_to_do_this_operation")}</p>
              </div>
            ) : (
              <div className="cash-in-layout-grid">
                <div className="cash-in-form-panel">
                  <div className="cash-in-panel-header">
                    <h2>{t("transfer_to_client")}</h2>
                    <p>{t("transfer_to_client_form_subtitle")}</p>
                  </div>

                  <div className="cash-in-form">
                    <div className="cash-in-form-group">
                      <label className="cash-in-form-label">{t("amount")}</label>
                      <div className="cash-in-input-wrapper">
                        <span className="cash-in-input-icon">
                          <i className="fa-solid fa-dollar-sign"></i>
                        </span>
                        <CustomInput
                          type="text"
                          placeholder={t("enter_amount")}
                          value={amount}
                          onChange={handleAmountChange}
                          className={`cash-in-input ${errors.amount ? "cash-in-input-error" : ""}`}
                          disabled={processing || !canProceed}
                        />
                      </div>
                      {errors.amount && <div className="cash-in-document-error-message">{errors.amount}</div>}
                    </div>

                    <div className="cash-in-form-group">
                      <label className="cash-in-form-label">{t("contact")}</label>
                      <CustomDropdown
                        options={clients}
                        value={selectedClient}
                        onChange={handleClientChange}
                        placeholder={t("select_client")}
                        name="client"
                        className={`cash-in-dropdown ${errors.client ? "dropdown-error" : ""}`}
                        disabled={processing || !canProceed}
                        searchable={true}
                        getDisplayValue={(option) => {
                          if (!option) return "";
                          if (typeof option === "object") return option.label || extractValue(option);
                          return String(option);
                        }}
                      />
                      {errors.client && <div className="cash-in-document-error-message">{errors.client}</div>}
                    </div>

                    <div className="cash-in-form-group">
                      <label className="cash-in-form-label">{t("wallet")}</label>
                      <CustomDropdown
                        options={wallets}
                        value={selectedWallet}
                        onChange={handleWalletChange}
                        placeholder={t("select_a_wallet")}
                        name="wallet"
                        className={`cash-in-dropdown ${errors.wallet ? "dropdown-error" : ""}`}
                        disabled={processing || !canProceed}
                        getDisplayValue={(option) => {
                          if (!option) return "";
                          if (typeof option === "object") return option.label || extractValue(option);
                          return String(option);
                        }}
                      />
                      {errors.wallet && <div className="cash-in-document-error-message">{errors.wallet}</div>}
                    </div>

                    <div className="cash-in-form-group">
                      <label className="cash-in-form-label">{t("note")}</label>
                      <CustomInput
                        type="text"
                        placeholder={t("transfer_note_placeholder")}
                        value={note}
                        onChange={handleNoteChange}
                        className={`cash-in-input ${errors.note ? "cash-in-input-error" : ""}`}
                        disabled={processing || !canProceed}
                      />
                      {errors.note && <div className="cash-in-document-error-message">{errors.note}</div>}
                    </div>
                  </div>

                  <div className="cash-in-actions">
                    <CustomButton
                      className="cash-in-proceed-button"
                      variant="primary"
                      onClick={handleProceed}
                      disabled={processing || !canProceed}
                    >
                      <span>{processing ? `${t("processing")}...` : t("proceed")}</span>
                    </CustomButton>
                  </div>
                </div>

                <div className="cash-in-side-panel">
                  <div className="cash-in-info-card">
                    <h3>
                      <i className="fa-solid fa-circle-info" />
                      {t("transfer_summary")}
                    </h3>
                    <ul className="cash-in-processing-list">
                      <li>
                        <span>
                          <i className="fa-solid fa-user" />
                          {t("selected_client")}
                        </span>
                        <strong>{selectedClient?.label || "-"}</strong>
                      </li>
                      <li>
                        <span>
                          <i className="fa-solid fa-wallet" />
                          {t("selected_wallet")}
                        </span>
                        <strong>{selectedWallet?.label || "-"}</strong>
                      </li>
                    </ul>
                  </div>

                  <div className="cash-in-help-card">
                    <h3>
                      <i className="fa-solid fa-headset" />
                      {t("contactus_title")}
                    </h3>
                    <p>{t("cash_in_help_description")}</p>
                    <button
                      type="button"
                      onClick={() =>
                        openSupportChatOnCurrentPage(
                          navigate,
                          location.pathname,
                          location.search
                        )
                      }
                    >
                      {t("contact_us")}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Password dialog – reuse same strategy as cash-in */}
      <TransactionPasswordDialog
        open={showPasswordPopup && !!passwordDialogData}
        onClose={handlePasswordDialogClose}
        mode="ib_transfer"
        requestToken={passwordDialogData?.requestToken ?? ""}
        firstMessage={passwordDialogData?.firstMessage ?? ""}
        secondMessage={passwordDialogData?.secondMessage ?? ""}
      />
    </div>
  );
}

